<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.incentives`) }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex class="text--capitalize" xs3>{{ $t('Common.company') }}</v-flex>
            <v-flex xs2>{{ $t('Views.IncentivesPage.discountType') }}</v-flex>
            <v-flex xs2>{{ $t('Views.IncentivesPage.value') }}</v-flex>
            <v-flex xs3>{{ $t('Views.IncentivesPage.requester') }}</v-flex>
            <v-flex xs2>{{ $t('Views.IncentivesPage.createdDate') }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile class="table__body__tr">
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex xs3>
                        <span class="text--ellipsis">
                          {{ item.subject }}
                          <span class="gray-6--text caption text--ellipsis">ID: {{ item.referenceId }}</span>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          {{ getIncentiveType(item.type) }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          {{ item.value | currencyBRL }}
                        </span>
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis" title="">
                          {{ item.createdBy }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          {{ moment(item.createdDate).format('DD/MM/YY') }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="employee"
          :text="$t('Common.emptyFinancial')"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { formatNumber, objectToArray } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    EmptyState
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.IncentivesPage.table',
      selectedItem: null
    }
  },
  methods: {
    getStatusList (val) {
      function getStatus (vm, value) {
        const match = objectToArray(vm.$t('Views.IncentivesPage.filters.statusList')).find(s => {
          return s.value === value
        })
        return match ? match.text : null
      }

      if (val === 0) {
        return getStatus(this, 'validationStarted') || '-'
      }

      if (val === 1) {
        return getStatus(this, 'validationFinished') || '-'
      }

      if (val === 2) {
        return getStatus(this, 'validationWithErrors') || '-'
      }

      if (val === 3) {
        return getStatus(this, 'pendingApproval') || '-'
      }

      if (val === 4) {
        return getStatus(this, 'approved') || '-'
      }

      if (val === 5) {
        return getStatus(this, 'executionStarted') || '-'
      }

      if (val === 6) {
        return getStatus(this, 'executionFinished') || '-'
      }

      return getStatus(this, 'executionFinishedWithErrors') || '-'
    },

    getIncentiveType (type) {
      const types = {
        PERCENT: this.$t('Views.IncentivesPage.viewIncentivesDialog.percent'),
        CREDIT: this.$t('Views.IncentivesPage.viewIncentivesDialog.credit'),
        SAAS_BY_RIDE: this.$t('Views.IncentivesPage.viewIncentivesDialog.saasByRide'),
        GRANT: this.$t('Views.IncentivesPage.viewIncentivesDialog.saasByRide')
      }

      return types[type] || type
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    }
  }
}
</script>
